import { useState } from "react";
import { MapPin, Lock, Layout, Send } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import SEOHead, { generateWebPageSchema } from "@/components/SEOHead";
import api from "@/lib/api";

const CATEGORIES = [
  "Car Rental", "Travel Insurance", "Tourism Boards", "Luggage & Gear",
  "Airlines / OTAs", "Fintech / Banking", "Vacation Rentals",
  "Family & Kids", "Outdoor / Adventure", "Credit Cards",
];

const TIERS = [
  { value: "", label: "Select a tier" },
  { value: "Market Page Sponsor ($2,500–$3,500/mo)", label: "Market Page Sponsor ($2,500–$3,500/mo)" },
  { value: "Regional Exclusive ($5,000–$7,000/mo)", label: "Regional Exclusive ($5,000–$7,000/mo)" },
  { value: "Category Exclusive ($12,000–$15,000/mo)", label: "Category Exclusive ($12,000–$15,000/mo)" },
  { value: "Not sure yet", label: "Not sure yet" },
];

const PLACEMENTS = [
  {
    title: "Market Page Sponsor",
    rate: "$2,500–$3,500/mo",
    features: [
      "Branded placement on 3–5 market pages of your choice",
      "Sidebar or in-content display unit",
      "Quarterly performance summary",
    ],
  },
  {
    title: "Regional Exclusive",
    rate: "$5,000–$7,000/mo",
    features: [
      "Category exclusivity across one region (Caribbean or US)",
      "Sidebar + in-content featured partner block",
      "Co-branded content opportunity",
    ],
  },
  {
    title: "Category Exclusive",
    rate: "$12,000–$15,000/mo",
    features: [
      "Sole brand in your vertical across ALL market pages",
      "Logo in header/footer partner bar",
      "Featured partner badge on all placements",
      "Co-branded content section",
      "Priority on new market page launches",
    ],
  },
];

export default function AdvertisePage() {
  const [form, setForm] = useState({
    company: "", contact: "", email: "", phone: "",
    category: "", tier: "", markets: "", message: "",
  });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await api.post("/advertise", {
        company: form.company, contact: form.contact, email: form.email,
        phone: form.phone, category: form.category, tier: form.tier,
        markets: form.markets, message: form.message,
      });
      setSent(true);
    } catch {
      toast.error("Something went wrong. Please try again or email advertise@paradiseborrowed.com.");
    } finally {
      setSending(false);
    }
  };

  const set = (key) => (e) => setForm({ ...form, [key]: e.target.value });
  const inputCls = "w-full px-4 py-2.5 text-sm border border-stone-300 rounded-lg focus:outline-none focus:border-coral transition-colors bg-white font-ui";

  const seoData = {
    title: "Advertise on Paradise Borrowed",
    description: "Reach tourists, local communities, and families across 26+ markets in the US and Caribbean. Category exclusivity available.",
    url: "https://www.paradiseborrowed.com/advertise",
  };

  return (
    <>
      <SEOHead title={seoData.title} description={seoData.description} canonicalUrl={seoData.url} schema={generateWebPageSchema(seoData)} />
      <div className="bg-[#FAFAF8]" data-testid="advertise-page">

        {/* A — Hero */}
        <div className="max-w-4xl mx-auto px-4 pt-16 pb-12 text-center">
          <p className="text-xs tracking-widest uppercase text-stone-400 mb-4 font-ui">Partnerships</p>
          <h1 className="text-4xl sm:text-5xl text-stone-900 mb-4" style={{ fontFamily: "'Fraunces', serif", fontWeight: 400 }}>
            Advertise on Paradise Borrowed
          </h1>
          <p className="text-stone-500 max-w-2xl mx-auto leading-relaxed font-ui">
            Reach tourists, local communities, and families across 26+ markets in the US and Caribbean. Category exclusivity available.
          </p>
        </div>

        {/* B — Why Advertise */}
        <div className="max-w-4xl mx-auto px-4 pb-12">
          <div className="grid md:grid-cols-3 gap-4">
            {[
              { icon: MapPin, title: "26+ Market Pages", desc: "Each destination has a dedicated, SEO-optimized landing page. Your brand appears where borrowers are actively searching." },
              { icon: Lock, title: "Category Exclusivity", desc: "Be the only brand in your vertical. Once a category is claimed, competitors are locked out for the full contract term." },
              { icon: Layout, title: "Native Placements", desc: "Sidebar display, featured partner blocks, and footer partner bar. Every placement is designed to feel part of the experience, not an interruption." },
            ].map((card) => (
              <div key={card.title} className="bg-white border border-stone-200 rounded-2xl p-6">
                <card.icon className="w-6 h-6 text-coral mb-3" />
                <h3 className="text-base text-stone-900 font-medium mb-2" style={{ fontFamily: "'Fraunces', serif" }}>{card.title}</h3>
                <p className="text-sm text-stone-500 leading-relaxed font-ui">{card.desc}</p>
              </div>
            ))}
          </div>
        </div>

        {/* C — Placement Types */}
        <div className="max-w-4xl mx-auto px-4 pb-12">
          <h2 className="text-2xl text-stone-900 mb-6 text-center" style={{ fontFamily: "'Fraunces', serif", fontWeight: 400 }}>Ad Placement Types</h2>
          <div className="grid md:grid-cols-3 gap-4">
            {PLACEMENTS.map((p, i) => (
              <div key={p.title} className={`bg-white border rounded-2xl p-6 flex flex-col ${i === 2 ? "border-coral border-2" : "border-stone-200"}`}>
                {i === 2 && <span className="text-xs font-medium text-coral uppercase tracking-wider mb-2 font-ui">Most Popular</span>}
                <h3 className="text-lg text-stone-900 font-medium mb-1" style={{ fontFamily: "'Fraunces', serif" }}>{p.title}</h3>
                <p className="text-2xl text-coral font-semibold mb-4 font-ui">{p.rate}</p>
                <ul className="space-y-2 flex-1">
                  {p.features.map((f) => (
                    <li key={f} className="text-sm text-stone-500 font-ui flex items-start gap-2">
                      <span className="w-1 h-1 rounded-full bg-coral mt-2 shrink-0" />
                      {f}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
          <p className="text-xs text-stone-400 text-center mt-4 font-ui">All tiers require a 12-month minimum commitment.</p>
        </div>

        {/* D — Available Categories */}
        <div className="max-w-4xl mx-auto px-4 pb-12">
          <h2 className="text-2xl text-stone-900 mb-6 text-center" style={{ fontFamily: "'Fraunces', serif", fontWeight: 400 }}>Available Categories</h2>
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
            {CATEGORIES.map((cat) => (
              <div key={cat} className="bg-white border border-stone-200 rounded-xl px-4 py-3 text-center">
                <p className="text-sm text-stone-700 font-medium font-ui">{cat}</p>
              </div>
            ))}
          </div>
          <p className="text-xs text-stone-400 text-center mt-4 font-ui">Each category is one exclusive partnership slot. First brand in, owns it.</p>
        </div>

        {/* E — Inquiry Form */}
        <div className="max-w-2xl mx-auto px-4 py-12">
          <div className="text-center mb-8">
            <h2 className="text-3xl text-stone-900 mb-3" style={{ fontFamily: "'Fraunces', serif", fontWeight: 400 }}>Get Started</h2>
            <p className="text-stone-500 text-sm font-ui">Submit an inquiry and we'll be in touch within 48 hours.</p>
          </div>

          {sent ? (
            <div className="bg-white border border-stone-200 rounded-2xl p-10 text-center" data-testid="advertise-success">
              <div className="w-14 h-14 rounded-full bg-emerald-50 text-emerald-600 flex items-center justify-center mx-auto mb-4">
                <Send className="w-6 h-6" />
              </div>
              <h3 className="text-xl text-stone-900 font-medium mb-2" style={{ fontFamily: "'Fraunces', serif" }}>Thank you for your inquiry</h3>
              <p className="text-stone-500 text-sm font-ui">We'll be in touch within 48 hours.</p>
            </div>
          ) : (
            <div className="bg-white border border-stone-200 rounded-2xl p-8" data-testid="advertise-form-card">
              <form onSubmit={handleSubmit} data-testid="advertise-form">
                <div className="grid sm:grid-cols-2 gap-4 mb-4">
                  <div>
                    <label className="block text-xs font-medium text-stone-700 mb-1.5 font-ui">Company Name *</label>
                    <input type="text" required value={form.company} onChange={set("company")} className={inputCls} data-testid="advertise-company" />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-stone-700 mb-1.5 font-ui">Contact Name *</label>
                    <input type="text" required value={form.contact} onChange={set("contact")} className={inputCls} data-testid="advertise-contact" />
                  </div>
                </div>
                <div className="grid sm:grid-cols-2 gap-4 mb-4">
                  <div>
                    <label className="block text-xs font-medium text-stone-700 mb-1.5 font-ui">Email Address *</label>
                    <input type="email" required value={form.email} onChange={set("email")} className={inputCls} data-testid="advertise-email" />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-stone-700 mb-1.5 font-ui">Phone Number</label>
                    <input type="text" value={form.phone} onChange={set("phone")} className={inputCls} data-testid="advertise-phone" />
                  </div>
                </div>
                <div className="grid sm:grid-cols-2 gap-4 mb-4">
                  <div>
                    <label className="block text-xs font-medium text-stone-700 mb-1.5 font-ui">Category Interest *</label>
                    <select required value={form.category} onChange={set("category")} className={inputCls} data-testid="advertise-category">
                      <option value="">Select a category</option>
                      {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
                      <option value="Other">Other</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-stone-700 mb-1.5 font-ui">Preferred Tier *</label>
                    <select required value={form.tier} onChange={set("tier")} className={inputCls} data-testid="advertise-tier">
                      {TIERS.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
                    </select>
                  </div>
                </div>
                <div className="mb-4">
                  <label className="block text-xs font-medium text-stone-700 mb-1.5 font-ui">Target Market Pages</label>
                  <textarea rows="2" value={form.markets} onChange={set("markets")} placeholder="Which markets or regions are you most interested in?" className={inputCls + " resize-none"} data-testid="advertise-markets" />
                </div>
                <div className="mb-6">
                  <label className="block text-xs font-medium text-stone-700 mb-1.5 font-ui">Message</label>
                  <textarea rows="3" value={form.message} onChange={set("message")} placeholder="Tell us about your brand and what you're looking for" className={inputCls + " resize-none"} data-testid="advertise-message" />
                </div>
                <Button type="submit" disabled={sending} className="w-full py-3 rounded-lg bg-coral hover:bg-[#d44814] text-white font-medium text-sm font-ui" data-testid="advertise-submit">
                  {sending ? "Submitting..." : "Submit Inquiry"}
                </Button>
              </form>
            </div>
          )}
        </div>
      </div>
    </>
  );
}
